// /admin/orgs/$orgSlug — one tenant, its keys, and the view-as entry point.
//
// ENUMERATION RULE: every failure that is not 401 renders the SAME headline.
// "No such workspace" versus "not yours" versus "not staff" would make this
// screen the oracle that `require_org` spent a 404-instead-of-403 to close.
// The slug appears in the title (the operator typed it) but never in a failure.

import {
  Badge,
  Button,
  Card,
  ErrorState,
  PageHeader,
  Skeleton,
  SkeletonStack,
  StatRow,
  StatTile,
  Table,
} from "../../ui";
import type { TableColumn } from "../../ui";
import { ConfirmButton } from "./ConfirmButton";
import { LicenseActions } from "./LicenseActions";
import { usePatchLicense, useTenant, viewAs } from "./api";
import type { LicenseKeyRow, TenantDetail } from "./api";
import "./admin.css";

export function TenantDetailScreen({ orgSlug }: { orgSlug: string }) {
  const tenant = useTenant(orgSlug);

  return (
    <div className="maisight-admin">
      <PageHeader
        eyebrow="Tenant"
        title={orgSlug}
        actions={
          <Button variant="primary" onClick={() => void viewAs(orgSlug)}>
            View as this tenant
          </Button>
        }
      />
      <Body orgSlug={orgSlug} query={tenant} />
    </div>
  );
}

function Body({
  orgSlug,
  query,
}: {
  orgSlug: string;
  query: ReturnType<typeof useTenant>;
}) {
  const patch = usePatchLicense(orgSlug);

  if (query.isPending) {
    return (
      <SkeletonStack label="Loading tenant">
        <Skeleton width={280} height={28} />
        <Skeleton width={620} />
      </SkeletonStack>
    );
  }

  // 401 is "signed out", a different screen from a failure — the doctrine
  // `screens/content/api.ts` states. Everything else collapses to one headline.
  const status = (query.error as { status?: number } | null)?.status;
  if (status === 401) {
    return (
      <ErrorState
        headline="You're signed out"
        detail="Sign in again to use the operator console."
        action={<Button to="/signin">Sign in</Button>}
      />
    );
  }
  if (query.isError || !query.data) {
    return <ErrorState headline="Could not load this tenant" />;
  }

  const detail: TenantDetail = query.data;
  const { tenant } = detail;

  return (
    <>
      <StatRow>
        <StatTile
          label="Plan"
          value={tenant.plan ?? "—"}
          detail={tenant.resolved_state}
        />
        <StatTile label="Documents" value={tenant.doc_count} />
        <StatTile
          label="Extraction cost"
          // cost_unknown means a read failed. Rendering $0.00 there would say
          // "this tenant cost nothing", which is a different claim.
          value={
            detail.cost_unknown || detail.cost_usd === null
              ? "—"
              : `$${detail.cost_usd.toFixed(2)}`
          }
          detail={detail.cost_unknown ? "partly unreadable" : undefined}
        />
      </StatRow>

      <Card tone="flat" header="License keys">
        <Table<LicenseKeyRow>
          label="License keys"
          columns={keyColumns(patch)}
          rows={detail.keys}
          rowKey={(row) => row.key_id}
          total={detail.keys.length}
          unit="keys"
          empty="No keys issued yet."
        />
        <LicenseActions orgSlug={orgSlug} keys={detail.keys} />
      </Card>
    </>
  );
}

function keyColumns(
  patch: ReturnType<typeof usePatchLicense>,
): TableColumn<LicenseKeyRow>[] {
  return [
    {
      key: "key_id",
      header: "Key",
      cell: (row) => <span className="type-code">{row.key_id}</span>,
    },
    { key: "plan", header: "Plan", cell: (row) => row.plan },
    {
      key: "status",
      header: "Status",
      cell: (row) => (
        <Badge tone={row.status === "active" ? "nominal" : "neutral"} lamp>
          {row.status}
        </Badge>
      ),
    },
    { key: "domains", header: "Domains", cell: (row) => row.domains.join(", ") || "—" },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (row) =>
        row.status === "active" ? (
          <ConfirmButton
            label="Revoke"
            confirmLabel="Confirm revoke"
            consequence={`Revoking stops mAiSight rendering on ${row.domains.join(", ") || "its bound domains"} immediately.`}
            pending={patch.isPending}
            onConfirm={() =>
              patch.mutate({ keyId: row.key_id, patch: { status: "canceled" } })
            }
          />
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              patch.mutate({ keyId: row.key_id, patch: { status: "active" } })
            }
          >
            Restore
          </Button>
        ),
    },
  ];
}
